import { useRoute } from "vue-router";
import type { AxiosInstance, AxiosStatic } from "axios";
import {
  KNOWN_DISALLOWED_CATALOG_IDS,
  KNOWN_ODP_CATALOG_IDS,
} from "./constants";
import { hvdCategoryFacetMock } from "@/mocks/hvdCategoryFacetMock";

type FacetsMap = Record<string, string[]>;

export function getSubdomainCatalogIdFromUrl(
  url: string,
  disallowedCatalogIds: string[] = KNOWN_DISALLOWED_CATALOG_IDS
): string {
  const host = new URL(url).host;
  const maybeSubdomain = getLeftmostSubdomain(host);
  return disallowedCatalogIds.includes(maybeSubdomain) ? "" : maybeSubdomain;
}

export function formatDatetime(date: string) {
  // return new Date(this.value).toLocaleDateString('de', { year: 'numeric', month: '2-digit', day: '2-digit' });
  // validate and format date to dd.mm.yyyy
  const dateObj = new Date(date);
  if (isNaN(dateObj.getTime())) {
    return "";
  }

  return dateObj.toLocaleDateString("de", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
}

/**
 * Maps some locale codes to German region
 */
export const localeCodeToGermanMini = {
  // Just add a bunch of European countries.
  // We'll just need 'de' anyways, right?
  de: "Deutsch",
  en: "Englisch",
  fr: "Französisch",
  es: "Spanisch",
  it: "Italienisch",
  pt: "Portugiesisch",
  nl: "Niederländisch",
  pl: "Polnisch",
  cs: "Tschechisch",
  da: "Dänisch",
  fi: "Finnisch",
  el: "Griechisch",
  hu: "Ungarisch",
  ga: "Irisch",
  lv: "Lettisch",
  lt: "Litauisch",
  mt: "Maltesisch",
  ro: "Rumänisch",
  sk: "Slowakisch",
  sl: "Slowenisch",
  sv: "Schwedisch",
  hr: "Kroatisch",
  bg: "Bulgarisch",
  et: "Estnisch",
};

export function getLeftmostSubdomain(hostname: string) {
  // Strip port number if present
  const domainWithoutPort = hostname.split(":")[0];

  // Construct regex patterns
  const secondLevelPattern =
    // "(co\\.uk|com\\.au|co\\.nz|co\\.za|com\\.br|staging\\.bydata\\.de|bydata\\.de)";
    "(co\\.uk|com\\.au|co\\.nz|co\\.za|com\\.br|staging\\.bydata\\.de|bydata\\.de)";
  // Update the generalPattern to match any subdomain before the main domain and TLD
  const generalPattern = new RegExp(
    `^(www\\.)?(.*?)\\.(?:[^\\.]+\\.${secondLevelPattern}|[^\\.]+\\.[^\\.]+)$`
  );
  const localPattern = /^([^.]+)\.local$/;

  // Match against .local pattern
  const localMatch = domainWithoutPort.match(localPattern);
  if (localMatch) {
    return localMatch[1];
  }

  // Match the subdomain for general domains
  const match = domainWithoutPort.match(generalPattern);

  if (match && match[2] && match[2] !== "www") {
    // Split the subdomains and return the leftmost one
    return match[2].split(".")[0];
  }

  return "";
}

export async function isOdp(catalogId, hubSearchApiBaseUrl) {
  if (KNOWN_DISALLOWED_CATALOG_IDS.includes(catalogId)) {
    return false;
  }

  if (KNOWN_ODP_CATALOG_IDS.includes(catalogId)) {
    return true;
  }

  const url = `${hubSearchApiBaseUrl}catalogues/${catalogId}`;
  const response = await fetch(url, { method: "GET" });
  return response.ok;
}

export function patchAxiosDatasetSearchWithOpenDataPresenceFacet({
  axiosInstance,
  hubSearchApiBaseUrl,
  openDataPresence,
}: {
  axiosInstance: AxiosInstance | AxiosStatic;
  hubSearchApiBaseUrl: string;
  openDataPresence: string;
}) {
  const route = useRoute();
  const interceptor = axiosInstance.interceptors.request.use(async (config) => {
    const isHubSearchApi = config?.url?.includes(hubSearchApiBaseUrl);
    const searchParams = config.params;
    const isDatasetSearch = searchParams?.filter === "dataset";

    if (
      !searchParams ||
      !hubSearchApiBaseUrl ||
      !isHubSearchApi ||
      !isDatasetSearch
    )
      return config;

    if (route.name !== "CatalogueDetails1") {
      return config;
    }

    const facets = searchParams?.facets;

    // const superCatalogFacet = facets?.["superCatalog"]?.[0] || "";
    const superCatalogFacet = facets?.["catalog"]?.[0] || "";
    const facetsWithOpenDataPresence = {
      ...facets,
      // ...(!!superCatalogFacet && {
      //   open_data_presence: [openDataPresence || superCatalogFacet],
      // }),
      // and also exclude superCatalog from the facets
      // also excluding catalog from the facet as it is in openbydata
      superCatalog: [],
      catalog: [openDataPresence],
    };
    config.params.facets = facetsWithOpenDataPresence;
    config.params.facetOperator = "OR";

    return config;
  });

  return interceptor;
}

export function patchAxiosDatasetSearchWithMockedHvdFacet({
  axiosInstance,
  hubSearchApiBaseUrl,
}: {
  axiosInstance: AxiosInstance | AxiosStatic;
  hubSearchApiBaseUrl: string;
}) {
  const interceptor = axiosInstance.interceptors.response.use((response) => {
    const facets = response?.data?.result?.facets;

    if (!Array.isArray(facets)) {
      return response;
    }

    const hvdFacetIndex = facets.findIndex((facet) => facet?.id === "hvdCategory");

    if (hvdFacetIndex === -1) {
      facets.push({
        id: hvdCategoryFacetMock.id,
        title: hvdCategoryFacetMock.title,
        items: hvdCategoryFacetMock.items,
      });
      return response;
    }

    facets[hvdFacetIndex] = {
      ...facets[hvdFacetIndex],
      id: hvdCategoryFacetMock.id,
      title: hvdCategoryFacetMock.title,
      items: hvdCategoryFacetMock.items,
    };

    if (response?.data?.result) {
      response.data.result.__hvdFacetMockApplied = true;
    }
    console.info("[HVD MOCK] Applied mocked hvdCategory facet", {
      items: hvdCategoryFacetMock.items.length,
    });

    return response;
  });

  return interceptor;
}

function isDatasetSearchRequest(config: any, hubSearchApiBaseUrl: string) {
  const isHubSearchApi = config?.url?.includes(hubSearchApiBaseUrl);
  const searchParams = config?.params;
  const isDatasetSearch =
    searchParams?.filter === "dataset" || searchParams?.filters === "dataset";

  return {
    isHubSearchApi,
    searchParams,
    isDatasetSearch,
  };
}

function normalizeFacetsForRequest(facets: any): FacetsMap | null {
  if (!facets || typeof facets !== "object" || Array.isArray(facets)) {
    return null;
  }

  const normalized = Object.keys(facets).reduce((acc: FacetsMap, key) => {
    const value = facets[key];
    if (Array.isArray(value)) {
      acc[key] = value;
    } else if (value == null) {
      acc[key] = [];
    } else {
      acc[key] = [value];
    }
    return acc;
  }, {});

  return normalized;
}

export function patchAxiosDatasetSearchForHvdTreeSelect({
  axiosInstance,
  hubSearchApiBaseUrl,
}: {
  axiosInstance: AxiosInstance | AxiosStatic;
  hubSearchApiBaseUrl: string;
}) {
  const route = useRoute();
  const interceptor = axiosInstance.interceptors.request.use((config: any) => {
    const { isHubSearchApi, searchParams, isDatasetSearch } =
      isDatasetSearchRequest(config, hubSearchApiBaseUrl);

    if (!isHubSearchApi || !isDatasetSearch || !searchParams) {
      return config;
    }

    if (route.name === "CatalogueDetails1") {
      return config;
    }

    if (typeof searchParams.facets === "string") {
      return config;
    }

    const normalizedFacets = normalizeFacetsForRequest(searchParams.facets);
    const hvdFacets = normalizedFacets?.["hvdCategory"];
    const hasHvdFacetSelection =
      Array.isArray(hvdFacets) && hvdFacets.length > 0;

    if (!hasHvdFacetSelection) {
      return config;
    }

    // Keep this normalization scoped to HVD tree-select requests only.
    config.params = {
      ...searchParams,
      filters: "dataset",
      facets: JSON.stringify(normalizedFacets),
    };

    delete config.params.filter;
    delete config.params.facetOperator;
    delete config.params.facetGroupOperator;

    return config;
  });

  return interceptor;
}
